/* eslint-disable react/jsx-pascal-case */
import React, { RefObject, createRef, useCallback, useEffect, useRef, useState } from 'react'
import { TableVirtuoso } from 'react-virtuoso'
import { createStyles, makeStyles } from '@material-ui/core/styles'
import Grid from '@material-ui/core/Grid'
import TableContainer from '@material-ui/core/TableContainer'
import Table from '@material-ui/core/Table'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import TableBody from '@material-ui/core/TableBody'
import O_TableBar from 'components/3_Organisms/O_TableBar'
import O_TableRowJokenMobileDetail from 'components/3_Organisms/O_TableRowJokenMobileDetail'
import O_TableHeaderJokenMobileDetail from 'components/3_Organisms/O_TableHeaderJokenMobileDetail'
import { useButtons, useCheckListSummary } from 'hooks/useJuryoTable'
import { organismGroupLog } from 'utils/logUtil'
import { useSelector } from 'store'
import { isUniqueJuryo } from 'utils/booleanUtil'
import { responseJuryo } from 'utils/requestUtil'
// import { useLocation } from 'react-router-dom'
import { voidF } from 'types/FunctionTypes'
import { useExpandDetail, useInitProcessDetail } from 'hooks/useJuryo'
import clsx from 'clsx'

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      width: '100%',
      display: 'flex',
    },
    tableRow: {
      backgroundColor: '#F3FFD8',
      '&not(:hover)': {
        backgroundColor: 'inherit',
      },
    },
    selectedTableRow: {
      backgroundColor: '#F3D7B4',
      '&not(:hover)': {
        backgroundColor: 'inherit',
      },
    },
    renkeiTableRow: {
      backgroundColor: '#DCDCDC',
      '&not(:hover)': {
        backgroundColor: 'inherit',
      },
    },
    renkeiSelectedTableRow: {
      backgroundColor: '#D3C3D3',
      '&not(:hover)': {
        backgroundColor: 'inherit',
      },
    },
  }),
)

type Props = {
  filterList: responseJuryo[]
  refetch: voidF
}

const O_TableJokenMobileDetail = ({ filterList, refetch }: Props) => {
  useEffect(() => {
    organismGroupLog('O_TableJokenMobileDetail')
  })
  const initProcess = useInitProcessDetail()
  useEffect(() => {
    initProcess()
  })

  const allCount = filterList.length

  const classes = useStyles()
  const [checkListJoken, allCheck] = useCheckListSummary(filterList)
  const buttonInfos = useButtons(filterList, allCount, { filename: '', message: '' }, refetch)
  const list = filterList

  const isAllChecked = filterList.length === 0 ? false : filterList.length === checkListJoken.length

  const [open, _changeOpen] = useExpandDetail()
  const headerRef = useRef<HTMLElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const [tableHeight, setTableHeight] = useState(window.innerHeight)
  // 新しいtableHeightを取得する
  const getNewTableHeight = () => {
    const el = headerRef.current
    const el2 = bottomRef.current
    if (el && el2) {
      // テーブルの高さを画面上部から画面下固定の要素までの距離-画面上部からテーブル要素までの距離
      const newTableHeight = Math.ceil(el2.getBoundingClientRect().top - el.getBoundingClientRect().top)
      return newTableHeight
    }
    return 0
  }

  // 新しいtableHeightを取得して更新する必要があれば更新する
  const changeTableHeight = () => {
    const newTableHeight = getNewTableHeight()
    if (newTableHeight > 0 && tableHeight !== newTableHeight) setTableHeight(newTableHeight)
  }
  useEffect(() => {
    // 描画時にテーブルの高さを調整
    // 条件開閉したときアニメーションでレイアウトが変わるので、tableHeightの値が変わらなくなるまで0.2秒間隔で処理を実行
    let oldTableHeight = -1
    const timerId = setInterval(() => {
      if (oldTableHeight !== tableHeight) {
        const nowTableHeight = getNewTableHeight()
        if (oldTableHeight === nowTableHeight) {
          changeTableHeight()
          clearInterval(timerId)
        } else {
          oldTableHeight = getNewTableHeight()
        }
      } else {
        oldTableHeight = getNewTableHeight()
      }
    }, 200)
  }, [open])
  useEffect(() => {
    // ウィンドウサイズ変更時にchangeTableHeightを呼び出すようにリスナーに登録
    window.removeEventListener('resize', changeTableHeight)
    window.addEventListener('resize', changeTableHeight)
    return () => {
      window.removeEventListener('resize', changeTableHeight)
    }
  }, [tableHeight])

  const getChecked = (joken: responseJuryo) => (isAllChecked ? true : checkListJoken.length <= 0 ? false : checkListJoken.some((r) => isUniqueJuryo(r, joken)))
  const table = (props: any) => <Table {...props} style={{ width: '700px', borderCollapse: 'separate' }} size="small" aria-label="purchases" />
  const tableHead = (props: any) => <TableHead {...props} style={{ ...props.style, zIndex: 2 }} ref={headerRef} /> // zIndexを指定しないとスクロール時に固定ヘッダーがデータ部分の下に入り込む
  const tableRow = (props: any) => {
    const isChecked = getChecked(props.item)
    const tableRowClasses = {
      [classes.tableRow]: props.item.RENKEI_KBN === 0 && !isChecked,
      [classes.selectedTableRow]: props.item.RENKEI_KBN === 0 && isChecked,
      [classes.renkeiTableRow]: props.item.RENKEI_KBN === 1 && !isChecked,
      [classes.renkeiSelectedTableRow]: props.item.RENKEI_KBN === 1 && isChecked,
    }
    return <TableRow hover role="checkbox" className={clsx(tableRowClasses)} {...props} />
  }
  const getComponent = useCallback(() => {
    return {
      Scroller: TableContainer,
      Table: table,
      TableHead: tableHead,
      TableRow: tableRow,
      TableBody: TableBody,
    }
  }, [checkListJoken.length])
  const selectJoken = useSelector((state) => state.TableJuryoReducer.selectList)

  // テーブルの固定項目用
  const refs = useRef<RefObject<HTMLTableCellElement>[]>([])
  if (refs.current.length === 0) {
    for (let i = 0; i < 2; i++) refs.current[i] = createRef<HTMLTableCellElement>()
  }

  return (
    <div className={classes.root}>
      <Grid container>
        <Grid item style={{ width: '100%' }}>
          <O_TableBar count={allCount} buttonInfos={buttonInfos} pagination={false} />
        </Grid>
        <TableVirtuoso
          style={{ height: tableHeight, overflowAnchor: 'none' }} // overflowAnchorは横スクロールした後に縦スクロールすると一番下まで自動でスクロールされる現象への対応
          data={list}
          components={getComponent()}
          fixedHeaderContent={() => <O_TableHeaderJokenMobileDetail click={allCheck} isChecked={isAllChecked} selectListCount={checkListJoken.length} refs={refs} />}
          itemContent={(_index, joken) => {
            const isChecked = getChecked(joken)
            const checkedJoken = selectJoken.filter((r) => isUniqueJuryo(r, joken))
            const NAIYO = checkedJoken.length > 0 ? checkedJoken[0].NAIYO : joken.NAIYO
            return <O_TableRowJokenMobileDetail joken={joken} isChecked={isChecked} NAIYO={NAIYO} selectListCount={checkListJoken.length} refs={refs} />
          }}
        />
      </Grid>
      <div style={{ visibility: 'hidden', position: 'fixed', bottom: 0, height: '1.5em' }} ref={bottomRef}></div>
    </div>
  )
}

export default React.memo(O_TableJokenMobileDetail)
